import { EventEmitter } from 'eventemitter3';
import { TUNING, type Side } from '../config/tuning';

export type LungRecoveryRate = 'idle' | 'combo' | 'none';

export class LungController extends EventEmitter {
  static EVT_CHANGED = 'lung:changed';
  static EVT_EMPTY = 'lung:empty';

  private capacity: Record<Side, number> = {
    player: TUNING.lung.maxCapacityPct,
    ai: TUNING.lung.maxCapacityPct
  };

  /** Refill lungs to full capacity. Optionally scoped to one side. */
  reset(side?: Side): void {
    if (side) {
      this.capacity[side] = TUNING.lung.maxCapacityPct;
      this.emit(LungController.EVT_CHANGED, side, this.capacity[side]);
      return;
    }
    this.capacity.player = TUNING.lung.maxCapacityPct;
    this.capacity.ai = TUNING.lung.maxCapacityPct;
    this.emit(LungController.EVT_CHANGED, 'player', this.capacity.player);
    this.emit(LungController.EVT_CHANGED, 'ai', this.capacity.ai);
  }

  /** Returns true if just hit empty. */
  drainInhale(side: Side, deltaSeconds: number, jointPct: number): boolean {
    const multiplier = LungController.drainMultiplierForJointPct(jointPct);
    const prev = this.capacity[side];
    const next = Math.max(0, prev - TUNING.lung.inhaleDepletionPctPerSec * multiplier * deltaSeconds);
    this.capacity[side] = next;
    this.emit(LungController.EVT_CHANGED, side, next);
    if (prev > 0 && next <= 0) {
      this.emit(LungController.EVT_EMPTY, side);
      return true;
    }
    return false;
  }

  /**
   * Cubic ramp: ~1.0× through the first half of the joint, then accelerates
   * sharply, hitting `jointShortMaxMultiplier` at empty.
   */
  static drainMultiplierForJointPct(jointPct: number): number {
    const ratio = Math.max(0, Math.min(100, jointPct)) / 100;
    const t = 1 - ratio;
    return 1 + (TUNING.lung.jointShortMaxMultiplier - 1) * t * t * t;
  }

  recover(side: Side, mode: LungRecoveryRate, deltaSeconds: number): void {
    if (mode === 'none') return;
    const rate = mode === 'combo'
      ? TUNING.lung.comboWindowRecoveryPctPerSec
      : TUNING.lung.idleRecoveryPctPerSec;
    const prev = this.capacity[side];
    const next = Math.min(TUNING.lung.maxCapacityPct, prev + rate * deltaSeconds);
    if (next !== prev) {
      this.capacity[side] = next;
      this.emit(LungController.EVT_CHANGED, side, next);
    }
  }

  get(side: Side): number {
    return this.capacity[side];
  }
}
